import { useEffect, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { useProject } from "@/components/projects";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  ProjectDocumentPicker,
  type PickerDocument,
  type PickerSection,
  type SectionSelections,
} from "@/components/project-document-picker";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { useAppNavigate } from "@/lib/use-app-navigate";

const formSchema = z.object({
  name: z.string().min(1, "Takeoff name is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface TakeoffPilotJobFormProps {
  onSuccess: (jobId: Id<"takeoffPilotJobs">) => void;
  cancelTo?: "/" | "/takeoffPilot" | "/takeoffPilot/new";
}

function makeInitialValues(): Record<string, SectionSelections> {
  return {
    schedules: { mode: "single", selection: null },
    plans: { mode: "multi", selections: [] },
  };
}

export function TakeoffPilotJobForm({
  onSuccess,
  cancelTo = "/takeoffPilot",
}: TakeoffPilotJobFormProps) {
  const navigate = useAppNavigate();
  const { projectId } = useProject();
  const createTakeoffJob = useMutation(
    api.takeoffPilot.jobs.createFromProjectRevisionsWithOperations
  );

  const [pickerValues, setPickerValues] = useState(makeInitialValues);
  const [pendingNoScheduleValues, setPendingNoScheduleValues] =
    useState<FormValues | null>(null);
  const [isNoScheduleConfirmOpen, setIsNoScheduleConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formResolver = zodResolver(
    formSchema as unknown as Parameters<typeof zodResolver>[0]
  ) as unknown as Resolver<FormValues>;

  const form = useForm<FormValues>({
    resolver: formResolver,
    defaultValues: { name: "" },
  });

  const { data: scheduleOptionsRaw } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listClassifiedPdfRevisionOptions,
      projectId ? { projectId, documentType: "schedule" } : "skip"
    )
  );
  const { data: planOptionsRaw } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listClassifiedPdfRevisionOptions,
      projectId ? { projectId, documentType: "plan_set" } : "skip"
    )
  );

  const scheduleDocs: PickerDocument[] = (scheduleOptionsRaw ?? []).map(
    (opt) => ({
      _id: String(opt._id),
      name: opt.name,
      revisions: (opt.revisions ?? []).map((r) => ({
        _id: String(r._id),
        revisionNumber: r.revisionNumber,
        revisionName: r.revisionName,
      })),
    })
  );
  const planDocs: PickerDocument[] = (planOptionsRaw ?? []).map((opt) => ({
    _id: String(opt._id),
    name: opt.name,
    revisions: (opt.revisions ?? []).map((r) => ({
      _id: String(r._id),
      revisionNumber: r.revisionNumber,
      revisionName: r.revisionName,
    })),
  }));

  const sections: PickerSection[] = [
    {
      key: "schedules",
      label: "Schedule",
      documents: scheduleDocs,
      selectionMode: "single",
      revisionMode: "single",
      optional: true,
    },
    {
      key: "plans",
      label: "Plan Documents",
      documents: planDocs,
      selectionMode: "multi",
      revisionMode: "single",
    },
  ];

  useEffect(() => {
    setPickerValues(makeInitialValues());
  }, [projectId]);

  const handlePickerChange = useCallback(
    (sectionKey: string, value: SectionSelections) => {
      setPickerValues((prev) => ({ ...prev, [sectionKey]: value }));
    },
    []
  );

  // Extract selected IDs from picker state
  const scheduleRevisionId =
    pickerValues.schedules?.mode === "single"
      ? (pickerValues.schedules.selection?.revisionId ?? "")
      : "";
  const selectedPlanRevisionIds =
    pickerValues.plans?.mode === "multi"
      ? pickerValues.plans.selections
          .map((s) => s.revisionId)
          .filter(Boolean)
      : [];

  const submitTakeoffJob = async (values: FormValues) => {
    if (!projectId) throw new Error("Please select a project first");
    const selectedSchedule =
      pickerValues.schedules?.mode === "single"
        ? pickerValues.schedules.selection
        : null;
    const selectedPlans =
      pickerValues.plans?.mode === "multi" ? pickerValues.plans.selections : [];
    if (selectedSchedule && !selectedSchedule.revisionId) {
      throw new Error("Select a revision for the selected schedule");
    }
    if (selectedPlans.some((selection) => !selection.revisionId)) {
      throw new Error("Select a revision for every selected plan document");
    }
    if (selectedPlanRevisionIds.length === 0) {
      throw new Error("Select at least one plan document revision");
    }

    setIsSubmitting(true);
    try {
      const jobId = await createTakeoffJob({
        projectId,
        name: values.name,
        ...(scheduleRevisionId
          ? {
              scheduleRevisionId:
                scheduleRevisionId as Id<"projectDocumentRevisions">,
            }
          : {}),
        planRevisionIds:
          selectedPlanRevisionIds as Id<"projectDocumentRevisions">[],
      });
      toast.success("TakeoffPilot job started");
      onSuccess(jobId);
    } catch (error) {
      toast.error(
        getUserFacingErrorMessage(error, "Failed to create takeoff job")
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    const selectedSchedule =
      pickerValues.schedules?.mode === "single"
        ? pickerValues.schedules.selection
        : null;
    const selectedPlans =
      pickerValues.plans?.mode === "multi" ? pickerValues.plans.selections : [];
    if (selectedSchedule && !selectedSchedule.revisionId) {
      toast.error("Select a revision for the selected schedule");
      return;
    }
    if (selectedPlans.some((selection) => !selection.revisionId)) {
      toast.error("Select a revision for every selected plan document");
      return;
    }
    const hasSchedule = !!scheduleRevisionId;
    const hasPlans = selectedPlanRevisionIds.length > 0;
    if (!hasSchedule && hasPlans) {
      setPendingNoScheduleValues(values);
      setIsNoScheduleConfirmOpen(true);
      return;
    }
    await submitTakeoffJob(values);
  };

  const confirmNoScheduleSubmit = () => {
    const values = pendingNoScheduleValues;
    setPendingNoScheduleValues(null);
    setIsNoScheduleConfirmOpen(false);
    if (values) void submitTakeoffJob(values);
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Takeoff Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Lobby Finish Takeoff"
                    {...field}
                    disabled={isSubmitting}
                  />
                </FormControl>
                <FormDescription>
                  Used for the job title and exported workbook name.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <ProjectDocumentPicker
            sections={sections}
            values={pickerValues}
            onChange={handlePickerChange}
            isLoading={
              scheduleOptionsRaw === undefined || planOptionsRaw === undefined
            }
            disabled={isSubmitting}
          />

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              Create TakeoffPilot Job
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() =>
                navigate({ to: cancelTo, title: "TakeoffPilot" })
              }
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
      <ConfirmationDialog
        open={isNoScheduleConfirmOpen}
        onOpenChange={(open) => {
          setIsNoScheduleConfirmOpen(open);
          if (!open) setPendingNoScheduleValues(null);
        }}
        title="Create without a schedule?"
        description={
          "No schedule tags will be extracted for this TakeoffPilot job. " +
          "You can still add custom line items and columns manually."
        }
        confirmLabel="Create Without Schedule"
        onConfirm={confirmNoScheduleSubmit}
        disabled={isSubmitting}
      />
    </>
  );
}
